import { useState } from "react";
import { request as apiRequest } from "@sharpana/auth/api_client/allauth";

export interface RestoreResult {
  /** Customer-facing SKU names of every subscription currently granting access. */
  activeSubscriptionSkuNames: string[];
  /** Remaining credit balance, as the backend reported it. */
  balance: unknown;
}

export interface UseRestorePurchasesResult {
  loading: boolean;
  /** Set when the call failed; the app decides what to say about it. */
  error: boolean;
  result: RestoreResult | null;
  restore: () => Promise<void>;
  reset: () => void;
}

/**
 * Re-sync purchases made outside this install (a reinstall, a second device).
 *
 * Returns DATA, not a sentence: the wording around it — which support address
 * to name, what the credits are called — is product copy and belongs in the
 * app that renders it.
 */
export function useRestorePurchases(): UseRestorePurchasesResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [result, setResult] = useState<RestoreResult | null>(null);

  const restore = async () => {
    if (loading) return;
    setLoading(true);
    setError(false);
    setResult(null);
    try {
      const data: any = await apiRequest("POST", "/api/restore-purchases");
      if (!data?.success) throw new Error(data?.error || "Restore failed");
      setResult({
        activeSubscriptionSkuNames: Array.isArray(
          data.active_subscriptions_sku_names
        )
          ? data.active_subscriptions_sku_names
          : [],
        balance: data?.balance,
      });
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setError(false);
    setResult(null);
  };

  return { loading, error, result, restore, reset };
}
